/**
 * Estrutura do Grupo: Procuradores e Sócios (ADR 0033). Pessoas que representam ou aprovam pelas
 * empresas do grupo, com classificações por empresa, poderes, limites, vigência, documentos
 * comprobatórios e validação jurídica. Cadastro não é autorização: só poder validado e vigente
 * vale. Aprovador interno e signatário são autorizações diferentes.
 */
import { z } from 'zod';
import { isValidCpf } from './partners.ts';

export const REPRESENTATION_KINDS = [
  'partner',
  'managing_partner',
  'non_partner_admin',
  'legal_representative',
  'attorney_in_fact',
  'master_approver',
  'threshold_approver',
  'other',
] as const;
/** Classificações de aprovação interna: não dão poderes de representação. */
export const APPROVER_KINDS = ['master_approver', 'threshold_approver'] as const;

export const SIGNATORY_POWERS = [
  'sign_commercial',
  'sign_services',
  'sign_nda',
  'sign_amendments',
  'sign_corporate',
  'sign_poa',
] as const;
export const APPROVER_POWERS = ['approve_contracts', 'approve_financial'] as const;
export const AUTHORITY_POWERS = [...SIGNATORY_POWERS, ...APPROVER_POWERS, 'other'] as const;
export type AuthorityPower = (typeof AUTHORITY_POWERS)[number];

/** Situação gravada; "vencida" e "ainda não iniciada" são calculadas pelas datas. */
export const AUTHORITY_STATUSES = [
  'draft',
  'pending_validation',
  'validated',
  'revoked',
  'suspended',
] as const;
export const AUTHORITY_EFFECTIVE_STATUSES = [
  ...AUTHORITY_STATUSES,
  'expired',
  'not_started',
] as const;
export const AUTHORITY_DOC_TYPES = [
  'articles',
  'articles_amendment',
  'minutes',
  'power_of_attorney',
  'substitution',
  'other',
] as const;

/**
 * Poder de assinatura exigido por modalidade jurídica (catálogo). Sem entrada, a modalidade não
 * pede signatário da LOOT.
 */
export const SERVICE_SIGNING_POWER: Record<string, (typeof SIGNATORY_POWERS)[number]> = {
  F01: 'sign_services',
  F02: 'sign_services',
  F03: 'sign_services',
  F04: 'sign_services',
  F05: 'sign_commercial',
  F06: 'sign_commercial',
  F07: 'sign_services',
  F08: 'sign_commercial',
  F09: 'sign_commercial',
  F10: 'sign_corporate',
  F11: 'sign_nda',
  F12: 'sign_nda',
  F13: 'sign_nda',
  F17: 'sign_poa',
  F18: 'sign_poa',
  F19: 'sign_amendments',
  F20: 'sign_amendments',
  F21: 'sign_commercial',
  F22: 'sign_amendments',
  // Revisão de terceiro: assinaturas só quando vira contratação (ADR 0063).
  F33: 'sign_commercial',
};

const text = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .transform((v) => (v === '' ? null : v))
    .nullable()
    .optional();
const day = z
  .string()
  .regex(/^(\d{4}-\d{2}-\d{2})?$/)
  .transform((v) => (v === '' ? null : v))
  .nullable()
  .optional();
const optionalId = z
  .uuid()
  .or(z.literal(''))
  .transform((v) => (v === '' ? null : v))
  .nullable()
  .optional();

export const representativeInput = z
  .object({
    fullName: z.string().trim().min(3).max(200),
    personKind: z.enum(['internal', 'external']),
    userId: optionalId,
    documentType: z.enum(['cpf', 'foreign', 'none']).default('none'),
    /** Documento em claro só na entrada: o servidor cifra e guarda os últimos dígitos. */
    document: text(40),
    email: text(254),
    jobTitle: text(200),
    status: z.enum(['active', 'inactive']).default('active'),
    notes: text(4000),
  })
  .refine((v) => v.personKind === 'internal' || !v.userId, {
    path: ['userId'],
    message: 'Pessoa externa não tem vínculo com colaborador',
  })
  .refine(
    (v) => v.documentType !== 'cpf' || !v.document || isValidCpf(v.document.replace(/\D/g, '')),
    {
      path: ['document'],
      message: 'CPF inválido',
    },
  )
  .refine((v) => !v.email || /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(v.email), {
    path: ['email'],
    message: 'E-mail inválido',
  });
export type RepresentativeInput = z.infer<typeof representativeInput>;

export const representationInput = z
  .object({
    legalEntityId: z.uuid(),
    kind: z.enum(REPRESENTATION_KINDS),
    kindDetail: text(200),
  })
  .refine((v) => v.kind !== 'other' || !!v.kindDetail, {
    path: ['kindDetail'],
    message: 'Descreva o vínculo',
  });

export const authorityInput = z
  .object({
    category: z.enum(['signatory', 'approver']),
    powers: z.array(z.enum(AUTHORITY_POWERS)).min(1).max(12),
    otherPower: text(300),
    serviceCodes: z
      .array(z.string().regex(/^F\d{2}$/))
      .max(40)
      .default([]),
    limitAmount: z
      .string()
      .regex(/^(\d{1,15}(\.\d{1,4})?)?$/, 'Use números com ponto (ex.: 50000.00)')
      .transform((v) => (v === '' ? null : v))
      .nullable()
      .optional(),
    limitCurrency: z
      .string()
      .regex(/^([A-Z]{3})?$/)
      .transform((v) => (v === '' ? null : v))
      .nullable()
      .optional(),
    validFrom: day,
    validUntil: day,
    signingMode: z.enum(['individual', 'joint']).default('individual'),
    minSigners: z.number().int().min(1).max(10).default(1),
    jointWith: z.array(z.uuid()).max(20).default([]),
    jointConditions: text(2000),
    restrictions: text(4000),
    legalNotes: text(4000),
  })
  .refine((v) => (v.limitAmount == null) === (v.limitCurrency == null), {
    path: ['limitCurrency'],
    message: 'Informe o limite e a moeda juntos',
  })
  .refine((v) => !v.validFrom || !v.validUntil || v.validUntil >= v.validFrom, {
    path: ['validUntil'],
    message: 'O fim não pode ser antes do início',
  })
  .refine((v) => (v.signingMode === 'individual' ? v.minSigners === 1 : v.minSigners >= 2), {
    path: ['minSigners'],
    message: 'Assinatura conjunta pede pelo menos 2 representantes; individual, 1',
  })
  .refine(
    (v) =>
      v.powers.every((p) =>
        p === 'other'
          ? true
          : v.category === 'signatory'
            ? (SIGNATORY_POWERS as readonly string[]).includes(p)
            : (APPROVER_POWERS as readonly string[]).includes(p),
      ),
    { path: ['powers'], message: 'Signatário tem poderes de assinatura; aprovador, de aprovação' },
  )
  .refine((v) => !v.powers.includes('other') || !!v.otherPower, {
    path: ['otherPower'],
    message: 'Descreva o outro poder',
  });
export type AuthorityInput = z.infer<typeof authorityInput>;

export const authorityDecisionInput = z
  .object({
    action: z.enum(['submit', 'validate', 'revoke', 'suspend', 'reactivate']),
    reason: text(2000),
  })
  .refine((v) => !['revoke', 'suspend'].includes(v.action) || !!v.reason, {
    path: ['reason'],
    message: 'Informe o motivo',
  });

export interface SignatoryOption {
  authorityId: string;
  representativeId: string;
  fullName: string;
  kind: string;
  effectiveStatus: string;
  signingMode: 'individual' | 'joint';
  minSigners: number;
  jointWith: string[];
  validUntil: string | null;
  limitAmount: string | null;
  limitCurrency: string | null;
  eligible: boolean;
  reason: string | null;
}

/**
 * Os signatários escolhidos bastam? Precisa de pelo menos um apto. Com assinatura conjunta, o
 * número mínimo de representantes aptos e, quando a autorização restringe, só com quem ela
 * permite. Não presume poderes: escolhido não apto (pendente, vencido, limite) não conta.
 */
export function signatoryReadiness(
  chosen: Pick<
    SignatoryOption,
    'representativeId' | 'signingMode' | 'minSigners' | 'jointWith' | 'eligible'
  >[],
): { ready: boolean; reason: 'none' | 'not_eligible' | 'joint_incomplete' | null } {
  if (chosen.length === 0) return { ready: false, reason: 'none' };
  const apt = chosen.filter((c) => c.eligible);
  if (apt.length === 0) return { ready: false, reason: 'not_eligible' };
  if (apt.some((c) => c.signingMode === 'individual')) return { ready: true, reason: null };
  const people = new Set(apt.map((c) => c.representativeId));
  const ok = apt.some((c) => {
    const partners = [...people].filter(
      (p) => p !== c.representativeId && (c.jointWith.length === 0 || c.jointWith.includes(p)),
    );
    return partners.length + 1 >= c.minSigners;
  });
  return ok ? { ready: true, reason: null } : { ready: false, reason: 'joint_incomplete' };
}

// ---------------------------------------------------------------------------
// Assinaturas e Testemunhas dos instrumentos jurídicos (ADR 0033)
// ---------------------------------------------------------------------------

/**
 * Natureza do instrumento para a composição das assinaturas: nacional (duas partes e duas
 * testemunhas) ou internacional (duas partes, testemunhas só quando o Jurídico indicar). Decidida
 * pelas partes e pela natureza jurídica, nunca pelo idioma ou pela moeda.
 */
export const SIGNING_JURISDICTIONS = ['domestic', 'international'] as const;
export type SigningJurisdiction = (typeof SIGNING_JURISDICTIONS)[number];

/**
 * Instrumentos unilaterais: só a LOOT se manifesta (procuração e revogação de procuração). Os
 * demais instrumentos com assinatura são bilaterais: exigem representantes das duas partes.
 */
export const SIGNING_UNILATERAL_SERVICES: readonly string[] = ['F17', 'F18'];
export type SigningStructure = 'bilateral' | 'unilateral';
export function signingStructure(serviceCode: string): SigningStructure | null {
  if (!SERVICE_SIGNING_POWER[serviceCode]) return null;
  return SIGNING_UNILATERAL_SERVICES.includes(serviceCode) ? 'unilateral' : 'bilateral';
}

/** Sugestão só quando a própria modalidade diz nacional ou internacional (pessoa confirma). */
export const SERVICE_JURISDICTION_HINT: Record<string, SigningJurisdiction> = {
  F01: 'domestic',
  F03: 'domestic',
  F05: 'domestic',
  F11: 'domestic',
  F02: 'international',
  F04: 'international',
  F06: 'international',
  F12: 'international',
};

/**
 * Quem participa da assinatura: signatário da LOOT (autorização do cadastro), signatário da
 * contraparte e as testemunhas indicadas por cada parte. Testemunha não precisa de poderes de
 * representação e não vira procurador, representante nem aprovador.
 */
export const SIGNER_ROLES = [
  'loot_signatory',
  'counterparty_signatory',
  'loot_witness',
  'counterparty_witness',
] as const;
export type SignerRole = (typeof SIGNER_ROLES)[number];
export const SIGNER_WITNESS_ROLES: readonly SignerRole[] = ['loot_witness', 'counterparty_witness'];

export const signerInput = z
  .object({
    /** Participante já gravado (mantém o CPF guardado quando nenhum novo é informado). */
    id: z.uuid().optional(),
    role: z.enum(SIGNER_ROLES),
    authorityId: optionalId,
    /** Testemunha interna: colaborador do cadastro de usuários. */
    userId: optionalId,
    fullName: text(200),
    email: z
      .email()
      .max(254)
      .or(z.literal(''))
      .transform((v) => (v === '' ? null : v))
      .nullable()
      .optional(),
    qualification: text(300),
    document: text(20),
  })
  .refine((v) => (v.role === 'loot_signatory') === !!v.authorityId, {
    path: ['authorityId'],
    message: 'O signatário da LOOT vem do cadastro Procuradores e Sócios',
  })
  .refine((v) => !v.userId || v.role === 'loot_witness', {
    path: ['userId'],
    message: 'Só a testemunha indicada pela LOOT pode ser um colaborador',
  })
  .refine((v) => v.role === 'loot_signatory' || !!v.userId || (v.fullName?.length ?? 0) >= 3, {
    path: ['fullName'],
    message: 'Informe o nome completo',
  })
  .refine((v) => v.role !== 'loot_signatory' || !v.document, {
    path: ['document'],
    message: 'O documento do signatário da LOOT fica no cadastro',
  })
  // Só CPF: números com pontos, traço ou espaços. Letras são recusadas (não apagadas), para o que
  // fica gravado ser o que foi digitado.
  .refine(
    (v) =>
      !v.document || (/^[\d.\s-]+$/.test(v.document) && isValidCpf(v.document.replace(/\D/g, ''))),
    { path: ['document'], message: 'CPF inválido' },
  );
export type SignerInput = z.infer<typeof signerInput>;

export const signingInput = z.object({
  jurisdiction: z
    .enum(SIGNING_JURISDICTIONS)
    .or(z.literal(''))
    .transform((v) => (v === '' ? null : v))
    .nullable()
    .optional(),
  signers: z.array(signerInput).max(20).default([]),
});

/** Exceção à composição padrão: só o Jurídico, com justificativa (null retira a exceção). */
export const signingExceptionInput = z.object({
  reason: z.string().trim().min(10).max(2000).nullable(),
});

export type SigningIssue =
  | 'jurisdiction'
  | 'loot_none'
  | 'loot_not_eligible'
  | 'loot_joint_incomplete'
  | 'counterparty_signatory'
  | 'loot_witness'
  | 'counterparty_witness'
  | 'witness_is_signatory';

/**
 * Pendências que a exceção justificada do Jurídico pode dispensar: só as testemunhas. Nunca os
 * poderes da LOOT nem a manifestação da contraparte em contrato bilateral.
 */
const WAIVABLE: readonly SigningIssue[] = ['loot_witness', 'counterparty_witness'];

const sameName = (a: string, b: string) =>
  a.normalize('NFD').replace(/\p{M}/gu, '').trim().toLowerCase().replace(/\s+/g, ' ') ===
  b.normalize('NFD').replace(/\p{M}/gu, '').trim().toLowerCase().replace(/\s+/g, ' ');

/**
 * A composição das assinaturas está pronta para a formalização?
 * - LOOT: signatários com poderes validados, vigentes e no limite; assinatura conjunta só quando
 *   a autorização exige (regra geral: um representante basta).
 * - Bilateral: representante da contraparte; nacional com uma testemunha indicada pela LOOT e
 *   uma pela contraparte; internacional sem testemunhas obrigatórias.
 * - Unilateral (procuração, revogação): só a LOOT; testemunhas opcionais.
 * - Testemunha não pode ser um dos signatários.
 * A exceção registrada pelo Jurídico dispensa só testemunhas. Ter os participantes não significa
 * que alguém assinou.
 */
export function signingComposition(input: {
  jurisdiction: SigningJurisdiction | null;
  signers: { role: SignerRole; fullName: string }[];
  lootChosen: Parameters<typeof signatoryReadiness>[0];
  exception: boolean;
  structure?: SigningStructure;
}): { ready: boolean; issues: SigningIssue[]; waived: SigningIssue[] } {
  const bilateral = (input.structure ?? 'bilateral') === 'bilateral';
  const issues: SigningIssue[] = [];
  if (bilateral && !input.jurisdiction) issues.push('jurisdiction');
  const loot = signatoryReadiness(input.lootChosen);
  if (!loot.ready) issues.push(`loot_${loot.reason!}` as SigningIssue);
  const of = (role: SignerRole) => input.signers.filter((s) => s.role === role);
  if (bilateral && of('counterparty_signatory').length === 0) {
    issues.push('counterparty_signatory');
  }
  if (bilateral && input.jurisdiction === 'domestic') {
    if (of('loot_witness').length === 0) issues.push('loot_witness');
    if (of('counterparty_witness').length === 0) issues.push('counterparty_witness');
  }
  const signers = input.signers.filter((s) => !SIGNER_WITNESS_ROLES.includes(s.role));
  const witnesses = input.signers.filter((s) => SIGNER_WITNESS_ROLES.includes(s.role));
  if (witnesses.some((w) => signers.some((s) => sameName(s.fullName, w.fullName)))) {
    issues.push('witness_is_signatory');
  }
  const waived = input.exception ? issues.filter((i) => WAIVABLE.includes(i)) : [];
  return { ready: issues.length === waived.length, issues, waived };
}
